import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';
import { useGoalDuplexer } from './use-goal-duplexer';
import type { WebGoalRound } from './goal-rounds';

export type { WebGoalRound };

// Client data layer for the workspace "Goals" tab. One fetch to /api/goals?workspaceId= returns the
// workspace's live goals, each enriched with its linked tasks ("the plan") and a task-derived percent.
// orion-web's CLIENT bundle must not import common-lib, so the shape lives here as a client-local contract
// matching what the route returns.

/** A goal's task — one "plan" row: its title, status, agent, and step checklist. */
export interface WebGoalTask {
  id: string;
  title: string;
  status: string;
  agentId?: string;
  steps: { title: string; completed: boolean }[];
  /** The instant the agent may not start before, ISO. Present only when the plan set one. */
  notBefore?: string;
  /** The tasks this one waits for — see `WebTask.blockedByTasks`. Feeds the plan's chip and the Timeline. */
  blockedByTasks?: import('./task-schedule').TaskBlockerRef[];
  /** The worker agent's own conversation, the durable record that an agent has been put on this task.
   *  Read only by the shared not-started rule; see `lib/task-schedule.ts`. */
  agentConversationId?: string;
}

/** A workspace goal for the Goals tab: identity + status, its percent, its tasks (the plan) and its
 *  member goals. A member is itself a goal — same shape, one level deep — carrying `parentGoalId`, its
 *  own tasks, its own completion condition and its own outcome. The route nests them, so this list is
 *  top-level goals only and a member can never leak into the grid. */
export interface WebGoal {
  id: string;
  title: string;
  brief: string;
  status: string;
  slug?: string;
  updatedDate?: string;
  /** When the goal was made — the tie-break between two deadlines on the same instant. */
  createdDate?: string;
  workspaceId?: string;
  conversationId?: string;
  percent: number;
  tasks: WebGoalTask[];
  /** The goal this one is a member of. Absent on a top-level goal. */
  parentGoalId?: string;
  /** What actually happened when it settled: REACHED / PARTLY_REACHED / OVERTAKEN / CALLED_OFF /
   *  DEADLINE_PASSED. Absent means nobody has said — never that the outcome was poor. */
  outcome?: string;
  /** GRAPH_EMPTIED / FIRST_CHILD_SUCCEEDS / MANUAL. Absent reads as GRAPH_EMPTIED. */
  completionCondition?: string;
  /** Whether the parent may not achieve while this member is unsettled. Members only. */
  requiredForParent?: boolean;
  /** This goal's members. Always empty on a member itself (one level, no recursion). */
  members?: WebGoal[];
  /** DAILY / WEEKLY / EVENT_DRIVEN. Present iff this member is STANDING — its work recurs in turns.
   *  Absent on an ordinary member, which has no rounds and shows no rounds section at all. */
  cadence?: string;
  /** The goal's end date (`completion_condition.deadline.ends_at`), ISO, when it has one. */
  endsAt?: string;
  /** The Goals-screen group the server put it in; absent from an older server (see `goalListGroupOf`). */
  listGroup?: 'in_progress' | 'ready' | 'done' | 'older';
  /** Past its end date and still open: its rounds are over, its wrap-up is running. */
  wrappingUp?: boolean;
  /** This member's turns. Present only on a standing member whose rounds were actually read; `[]` means
   *  it genuinely has none yet. ABSENT means we did not learn — see `lib/goal-rounds.ts`. */
  rounds?: WebGoalRound[];
  /** True when the rounds read failed for this standing member. Never render an empty state for it. */
  roundsUnavailable?: boolean;
}

/** A goal's members, defaulted — the route omits nothing, but a cached payload from before members
 *  existed carries no key, and a plan is not "no members" because a field is missing from an old
 *  response. Reading through one accessor keeps every call site out of that question. */
export function goalMembers(goal: WebGoal): WebGoal[] {
  return goal.members ?? [];
}

/** How many of a goal's members are settled as done. Mirrors the percent rule: only ACHIEVED counts —
 *  an abandoned member settled without delivering, and `outcome` is what says so. */
export function membersDone(members: WebGoal[]): number {
  return members.filter((member) => member.status === 'ACHIEVED').length;
}

/** Find a goal by id across the top level AND one level of members, so a member opened from its
 *  parent's panel keeps re-selecting the live copy on every refetch exactly like a top-level goal does. */
export function findGoal(goals: WebGoal[], goalId: string): WebGoal | undefined {
  for (const goal of goals) {
    if (goal.id === goalId) return goal;
    const member = goalMembers(goal).find((candidate) => candidate.id === goalId);
    if (member) return member;
  }
  return undefined;
}

export type WorkspaceGoalsState =
  | { status: 'loading' }
  | { status: 'ready'; goals: WebGoal[] }
  | { status: 'error' };

/** `workspaceId` omitted reads every live goal across every Space — the top-level Goals screen's source,
 *  mirroring `lib/tasks.ts`'s account-wide `fetchTasks()`. */
async function fetchWorkspaceGoals(workspaceId: string | undefined): Promise<WebGoal[]> {
  const query = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
  const res = await apiFetch(`${API_BASE}/api/goals${query}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { goals?: WebGoal[] };
  return data.goals ?? [];
}

/** Warm the account-wide goals list into the React Query cache on app mount, so the Your Agents summary
 *  tile doesn't sit on a spinner. Best-effort; swallows errors. */
export function prefetchWorkspaceGoals(): void {
  void queryClient
    .prefetchQuery({ queryKey: queryKeys.workspaceGoals(undefined), queryFn: () => fetchWorkspaceGoals(undefined) })
    .catch(() => {});
}

/** `workspaceId` omitted fetches account-wide (the top-level Goals screen); passed, it scopes to one
 *  Space (a workspace's Dashboard tile, the Goal detail drill-in). Always enabled — unlike the old
 *  workspace-only hook, there is no "nothing to fetch yet" state once the id is optional. */
export function useWorkspaceGoals(workspaceId?: string): WorkspaceGoalsState {
  const query = useQuery({
    queryKey: queryKeys.workspaceGoals(workspaceId),
    queryFn: () => fetchWorkspaceGoals(workspaceId),
  });

  // A goal created/updated/deleted refreshes the list live.
  useGoalDuplexer(() => void query.refetch());

  // The goal detail plan and percent are derived from linked AgentTasks.
  useAgentTaskDuplexer(() => void query.refetch());

  // Keep a prior good list on a transient refetch failure — only surface an error before the first success.
  if (query.data) return { status: 'ready', goals: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}

/**
 * Which i18n key seeds the side chat behind the Goals tab's "+ New goal" affordance.
 *
 * The affordance opens the chat with a message the user appears to have typed, and the agent takes it
 * from there (asks the one or two things it needs, then lays out the goal AND its task plan). There is
 * deliberately NO form: `ManageGoal` wants a `brief` that stands on its own ("whoever picks it up later
 * works from this alone") plus a task plan, and a form produces neither — a goal created with an empty
 * plan is the blank-card failure this codebase already has history with.
 *
 * Two keys, because the seed names the Space and the Space name is not always known:
 * - `goals.create.seed` ("I want to add a new goal to my {workspace} Space") — the scoped seed, so the
 *   agent creates the goal in THIS Space without asking. Same key and same copy as native iOS/Android.
 * - `goals.createPrompt` — the unscoped fallback, used when the workspace name has not resolved yet.
 *   Without it the scoped template would interpolate an empty string and the user would appear to have
 *   typed "...to my  Space", or worse, a literal "{workspace}".
 *
 * Pure and exported so the fallback is unit-testable without rendering the page.
 */
export function newGoalSeedKey(workspaceName: string | undefined): 'goals.create.seed' | 'goals.createPrompt' {
  return (workspaceName ?? '').trim() ? 'goals.create.seed' : 'goals.createPrompt';
}

/**
 * The HIDDEN context line the "+ New goal" handoff carries, naming the Space by **id**.
 *
 * Byte-identical to the phones' `createGoalContext` (iOS `AppRouter.swift`, Android `ChatContext.kt`) —
 * one product, one sentence, three surfaces; `new-goal-affordance.test.ts` reads both native sources and
 * pins that. It rides `ChatHandoffNew.context`, i.e. the harness's top-level `context` field, so it is
 * never text the user appears to have typed.
 *
 * Why an id and not the name the seed already says: a name is a display string. It can be ambiguous
 * between two Spaces, it can be reworded by the user before the message sends, and — the case that
 * actually degraded the seed — it may not have resolved yet, in which case the visible copy falls back to
 * the unscoped prompt and the agent has to ASK which Space. With the context carrying the id, an
 * unresolved name costs the user a slightly vaguer sentence and costs the agent nothing.
 *
 * Returns undefined when there is no workspace id: there is then nothing stable to scope the create to,
 * and a context line naming an empty id is worse than none. Mirrors the phones' `nil`/`null`.
 */
export function createGoalContext(workspaceId: string | undefined, workspaceName: string): string | undefined {
  if (!workspaceId) return undefined;
  return (
    `The user wants to add a new goal to their "${workspaceName}" Space (workspace id "${workspaceId}"). ` +
    'Help them define and create a new goal in this Space. Use ManageGoal to create it under this exact ' +
    "workspace_id. Don't ask which Space."
  );
}
